// src/components/Header.jsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import IconSearch from "../assets/search.svg";
import IconBaggageClaim from "../assets/baggage-claim.svg";
import Logo from "../assets/logo.svg";
import UserDemo from "../assets/user-demo.png";
import { FiMenu, FiX, FiChevronDown, FiMapPin } from "react-icons/fi";
import { authService } from "../services/auth";
import { useCustomerAddresses } from "../hooks/useCustomerAddresses";
import { useSelectedLocation } from "../context/LocationContext";

const Header = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isAuthMenuOpen, setIsAuthMenuOpen] = useState(false);
  const [isAddressDropdownOpen, setIsAddressDropdownOpen] = useState(false);
  const [dropdownSelectedAddressId, setDropdownSelectedAddressId] =
    useState(null);

  const currentUser = authService.getCurrentUser();
  const isAuthenticated = authService.isAuthenticated();
  const {
    addresses,
    loading: addressesLoading,
    error: addressesError,
    refetchAddresses,
  } = useCustomerAddresses();
  const {
    selectedLocation,
    updateSelectedLocation,
    clearSelectedLocation,
    isLoadingLocation,
  } = useSelectedLocation();

  const dropdownRef = useRef(null);
  const userMenuRef = useRef(null);
  const authMenuRef = useRef(null);

  useEffect(() => {
    if (selectedLocation) {
      setDropdownSelectedAddressId(selectedLocation.addressId || null);
    } else {
      setDropdownSelectedAddressId(null);
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (
      isAuthenticated &&
      isAddressDropdownOpen &&
      addresses.length === 0 &&
      !addressesLoading
    ) {
      refetchAddresses();
    }
  }, [
    isAuthenticated,
    isAddressDropdownOpen,
    addresses.length,
    addressesLoading,
    refetchAddresses,
  ]);

  const handleClickOutside = useCallback((event) => {
    const locationButton = document.getElementById("location-button");
    if (
      locationButton &&
      !locationButton.contains(event.target) &&
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target)
    ) {
      setIsAddressDropdownOpen(false);
    }
    const userButton = document.getElementById("user-menu-button");
    if (
      userButton &&
      !userButton.contains(event.target) &&
      userMenuRef.current &&
      !userMenuRef.current.contains(event.target)
    ) {
      setIsUserMenuOpen(false);
    }
    const authButton = document.getElementById("auth-menu-button");
    if (
      authButton &&
      !authButton.contains(event.target) &&
      authMenuRef.current &&
      !authMenuRef.current.contains(event.target)
    ) {
      setIsAuthMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleLogout = () => {
    authService.logout();
    clearSelectedLocation();
    setIsUserMenuOpen(false);
    setIsAuthMenuOpen(false);
    setIsAddressDropdownOpen(false);
  };

  const handleApplyLocation = () => {
    if (!dropdownSelectedAddressId) {
      alert("Lütfen bir adres seçin.");
      return;
    }
    const addressToApply = addresses.find(
      (addr) => addr.id === dropdownSelectedAddressId
    );

    if (addressToApply) {
      const locationData = {
        addressId: addressToApply.id,
        cityId: addressToApply.city_id,
        districtId: addressToApply.district_id,
        cityName: addressToApply.city_name,
        districtName: addressToApply.district_name,
        title: addressToApply.title,
        fullAddress: addressToApply.address,
      };
      updateSelectedLocation(locationData);
      setIsAddressDropdownOpen(false);
      alert("Konum güncellendi!");
    } else {
      alert("Seçilen adres bilgisi bulunamadı.");
    }
  };

  const displayLocationText = isLoadingLocation
    ? "Konum..."
    : selectedLocation
    ? selectedLocation.title ||
      `${selectedLocation.cityName || ""}, ${
        selectedLocation.districtName || ""
      }`
    : "Konum Seç";

  return (
    <header className="w-full bg-white border-b border-gray-200 relative z-30">
      <div className="max-w-7xl mx-auto px-4">
        <div className="hidden md:flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex flex-col items-center flex-shrink-0 mr-4"
          >
            <div className="w-32">
              <img src={Logo} alt="Yıkafoni Logo" className="w-full h-auto" />
            </div>
            <span className="font-sans text-[10px] sm:text-[11.43px] leading-[20px] sm:leading-[26.67px] text-[#4F46E5] font-bold whitespace-nowrap">
              TEMİZLİK BURADA
            </span>
          </Link>

          {/* Search Bar */}
          <div className="flex-1 max-w-xl mx-4 hidden md:block">
            <div className="relative flex items-center">
              <input
                type="text"
                placeholder="Hizmet, kategori veya firma ara"
                className="w-full h-12 pl-4 pr-12 text-sm border border-gray-200 rounded-full focus:outline-none bg-white"
              />
              <button className="absolute right-2 p-2">
                <img src={IconSearch} alt="Search" className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-2 sm:space-x-4 ml-auto">
            {/* Location Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                id="location-button"
                className={`flex items-center space-x-1 px-2 sm:px-3 py-2 rounded-full border ${
                  isAuthenticated
                    ? "hover:bg-gray-50 border-gray-100 cursor-pointer"
                    : "border-gray-100 text-gray-400 cursor-not-allowed"
                }`}
                onClick={() =>
                  isAuthenticated &&
                  setIsAddressDropdownOpen(!isAddressDropdownOpen)
                }
                disabled={!isAuthenticated || isLoadingLocation}
              >
                <FiMapPin
                  className={`w-5 h-5 flex-shrink-0 ${
                    isAuthenticated ? "text-gray-500" : "text-gray-300"
                  }`}
                />
                <span
                  className={`text-xs sm:text-sm truncate max-w-[80px] sm:max-w-[150px] ${
                    !isAuthenticated && "italic"
                  }`}
                >
                  {displayLocationText}
                </span>
                {isAuthenticated && (
                  <FiChevronDown
                    className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 transition-transform duration-200 ${
                      isAddressDropdownOpen ? "transform rotate-180" : ""
                    }`}
                  />
                )}
              </button>
              {isAuthenticated && isAddressDropdownOpen && (
                <div className="absolute right-0 md:left-0 mt-2 w-72 bg-white rounded-lg shadow-lg p-4 z-40 border border-gray-100 max-h-80 overflow-y-auto">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 border-b pb-2">
                    Kayıtlı Adreslerim
                  </h4>
                  {addressesLoading && (
                    <p className="text-xs text-gray-500 text-center py-2">
                      Adresler yükleniyor...
                    </p>
                  )}
                  {addressesError && (
                    <p className="text-xs text-red-500 text-center py-2">
                      Adresler yüklenemedi.
                    </p>
                  )}
                  {!addressesLoading &&
                    !addressesError &&
                    addresses.length === 0 && (
                      <p className="text-xs text-gray-500 text-center py-2">
                        Kayıtlı adresiniz yok.
                      </p>
                    )}
                  {!addressesLoading &&
                    !addressesError &&
                    addresses.length > 0 && (
                      <ul className="space-y-2">
                        {addresses.map((address) => (
                          <li
                            key={address.id}
                            className={`p-3 rounded-md cursor-pointer transition-colors duration-150 ${
                              dropdownSelectedAddressId === address.id
                                ? "bg-indigo-100 border border-indigo-200"
                                : "hover:bg-gray-50 border border-transparent"
                            }`}
                            onClick={() =>
                              setDropdownSelectedAddressId(address.id)
                            }
                          >
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {address.title || "Başlıksız Adres"}
                            </p>
                            <p className="text-xs text-gray-600 truncate mt-1">
                              {address.address || ""}
                            </p>
                            <p className="text-xs text-gray-500">
                              {address.district_name || ""} /{" "}
                              {address.city_name || ""}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                  <Link
                    to="/addresses"
                    className="block text-center text-xs text-indigo-600 hover:underline mt-3 pt-3 border-t"
                    onClick={() => setIsAddressDropdownOpen(false)}
                  >
                    Adresleri Yönet / Yeni Ekle
                  </Link>
                  <button
                    className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors disabled:bg-gray-400 mt-4"
                    onClick={handleApplyLocation}
                    disabled={!dropdownSelectedAddressId || addressesLoading}
                  >
                    Seçili Adresi Uygula
                  </button>
                </div>
              )}
            </div>

            {/* User/Auth Section */}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  id="user-menu-button"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 hover:bg-gray-50 px-2 sm:px-3 py-2 rounded-full border border-gray-100"
                >
                  <div className="w-8 h-8 rounded-full bg-indigo-500 text-white flex items-center justify-center">
                    <span className="text-sm font-semibold">
                      {currentUser?.full_name
                        ? currentUser.full_name.charAt(0).toUpperCase()
                        : " "}
                    </span>
                  </div>
                  <span className="text-xs sm:text-sm truncate max-w-[70px] sm:max-w-[100px]">
                    {currentUser?.full_name}
                  </span>
                  <FiChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-100">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Profilim
                    </Link>
                    <Link
                      to="/orders"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Siparişlerim
                    </Link>
                    <Link
                      to="/addresses"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Adreslerim
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50"
                    >
                      Çıkış Yap
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative" ref={authMenuRef}>
                <button
                  id="auth-menu-button"
                  onClick={() => setIsAuthMenuOpen(!isAuthMenuOpen)}
                  className="flex items-center space-x-2 hover:bg-gray-50 px-2 sm:px-3 py-2 rounded-full border border-gray-100"
                >
                  <div className="text-xs sm:text-sm whitespace-nowrap">
                    <span className="font-semibold">Giriş Yap</span>
                    <span className="text-gray-500 text-[10px] sm:text-xs ml-1">
                      veya
                    </span>
                    <span className="text-gray-500 text-[10px] sm:text-xs ml-1">
                      Üye Ol
                    </span>
                  </div>
                  <FiChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>

                {isAuthMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg p-4 z-50 border border-gray-100">
                    <div className="text-center mb-4">
                      <h3 className="text-base font-semibold text-gray-900">
                        Hoş Geldiniz!
                      </h3>
                      <p className="text-sm text-gray-500">
                        Devam etmek için giriş yapın veya hesap oluşturun.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <Link
                        to="/auth/login"
                        className="block w-full text-center px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors"
                        onClick={() => setIsAuthMenuOpen(false)}
                      >
                        Giriş Yap
                      </Link>
                      <Link
                        to="/auth/register"
                        className="block w-full text-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        onClick={() => setIsAuthMenuOpen(false)}
                      >
                        Hesap Oluştur
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Cart */}
            <Link
              to="/sepetim"
              className="flex items-center space-x-2 hover:bg-gray-50 px-2 sm:px-3 py-2 rounded-full border border-gray-100"
            >
              <div className="relative">
                <img src={IconBaggageClaim} alt="Cart" className="w-5 h-5" />
                <span className="absolute -top-2 -right-2 w-4 h-4 sm:w-5 sm:h-5 bg-[#6366F1] text-white text-[10px] sm:text-xs font-bold rounded-full flex items-center justify-center">
                  1
                </span>
              </div>
              <span className="text-xs sm:text-sm hidden sm:block">
                Sepetim
              </span>
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden">
        <div className="flex items-center justify-between p-4">
          <Link to="/" className="flex flex-col items-center">
            <img src={Logo} alt="Yıkafoni Logo" className="w-28 h-auto" />
            <span className="font-sans text-[11.43px] leading-[26.67px] text-[#4F46E5] font-bold">
              TEMİZLİK BURADA
            </span>
          </Link>
          <button onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-gray-200 py-2">
            <div className="px-4 py-2">
              <input
                type="text"
                placeholder="Hizmet, kategori veya firma ara"
                className="w-full h-10 px-4 text-sm border border-gray-200 rounded-full"
              />
            </div>
            <nav className="space-y-1">
              <button className="w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center justify-between">
                <span>{displayLocationText}</span>
                <FiChevronDown className="w-4 h-4" />
              </button>
              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-sm hover:bg-gray-50"
                    onClick={() => setMenuOpen(false)}
                  >
                    Profilim
                  </Link>
                  <Link
                    to="/orders"
                    className="block px-4 py-2 text-sm hover:bg-gray-50"
                    onClick={() => setMenuOpen(false)}
                  >
                    Siparişlerim
                  </Link>
                  <Link
                    to="/addresses"
                    className="block px-4 py-2 text-sm hover:bg-gray-50"
                    onClick={() => setMenuOpen(false)}
                  >
                    Adreslerim
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMenuOpen(false);
                    }}
                    className="w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-gray-50"
                  >
                    Çıkış Yap
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/auth/login"
                    className="block px-4 py-2 text-sm hover:bg-gray-50"
                    onClick={() => setMenuOpen(false)}
                  >
                    Giriş Yap
                  </Link>
                  <Link
                    to="/auth/register"
                    className="block px-4 py-2 text-sm hover:bg-gray-50"
                    onClick={() => setMenuOpen(false)}
                  >
                    Üye Ol
                  </Link>
                </>
              )}

              <Link
                to="/sepetim"
                className="block px-4 py-2 text-sm hover:bg-gray-50"
                onClick={() => setMenuOpen(false)}
              >
                Sepetim
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
